import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TASK_STATUS_LABELS } from "@/lib/constants";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

export default function ReportsPage() {
  const [global, setGlobal] = useState(null);
  const [dash, setDash] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [userReport, setUserReport] = useState(null);
  const [teamReport, setTeamReport] = useState(null);
  const [projectReport, setProjectReport] = useState(null);

  // User Work Report States
  const [userFilterType, setUserFilterType] = useState("all");
  const [userFromDate, setUserFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [userToDate, setUserToDate] = useState(new Date().toISOString().split("T")[0]);
  const [userTeamId, setUserTeamId] = useState("");
  const [userProjectId, setUserProjectId] = useState("");
  const [userWorkReport, setUserWorkReport] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [g, d, u, t, p] = await Promise.all([
          api.get("/reports/tasks-global"),
          api.get("/reports/dashboard"),
          api.get("/users/all"),
          api.get("/teams/all"),
          api.get("/projects/all"),
        ]);
        setGlobal(g.data); setDash(d.data);
        setUsers(u.data); setTeams(t.data); setProjects(p.data);
        if (u.data[0]) setSelectedUser(u.data[0].id);
        if (t.data[0]) { setSelectedTeam(t.data[0].id); setUserTeamId(t.data[0].id); }
        if (p.data[0]) { setSelectedProject(p.data[0].id); setUserProjectId(p.data[0].id); }
      } catch {}
    })();
  }, []);

  useEffect(() => { if (selectedUser) api.get(`/reports/user/${selectedUser}`).then(r => setUserReport(r.data)).catch(() => {}); }, [selectedUser]);
  
  useEffect(() => {
    if (!selectedUser) return;
    let url = `/reports/user/${selectedUser}/work-report?filter_type=${userFilterType}&from_date=${userFromDate}&to_date=${userToDate}`;
    if (userFilterType === "team" && userTeamId) {
      url += `&team_id=${userTeamId}`;
    } else if (userFilterType === "project" && userProjectId) {
      url += `&project_id=${userProjectId}`;
    }
    api.get(url).then(r => setUserWorkReport(r.data)).catch(() => setUserWorkReport(null));
  }, [selectedUser, userFilterType, userFromDate, userToDate, userTeamId, userProjectId]);

  useEffect(() => { if (selectedTeam) api.get(`/reports/team/${selectedTeam}`).then(r => setTeamReport(r.data)).catch(() => {}); }, [selectedTeam]);
  useEffect(() => { if (selectedProject) api.get(`/reports/project/${selectedProject}`).then(r => setProjectReport(r.data)).catch(() => {}); }, [selectedProject]);

  const globalData = global ? Object.entries(global).map(([k, v]) => ({ name: TASK_STATUS_LABELS[k] || k, value: v })) : [];
  const pieData = dash ? Object.entries(dash.tasks_by_priority).map(([k, v]) => ({ name: k, value: v })) : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Slice performance by user, team, project, or globally.</p>
      </div>

      <Tabs defaultValue="global" className="space-y-4">
        <TabsList>
          <TabsTrigger value="global" data-testid="reports-tab-global">Global</TabsTrigger>
          <TabsTrigger value="user" data-testid="reports-tab-user">User</TabsTrigger>
          <TabsTrigger value="team" data-testid="reports-tab-team">Team</TabsTrigger>
          <TabsTrigger value="project" data-testid="reports-tab-project">Project</TabsTrigger>
        </TabsList>

        <TabsContent value="global" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Global Task State</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={globalData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Tasks by Priority</div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2} dataKey="value">
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        <TabsContent value="user" className="space-y-4">
          <div className="flex flex-wrap items-end gap-3 bg-card p-4 rounded-lg border">
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <span className="text-xs font-semibold text-muted-foreground">Select User</span>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger data-testid="report-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 w-44">
              <span className="text-xs font-semibold text-muted-foreground">Filter Type</span>
              <Select value={userFilterType} onValueChange={setUserFilterType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="team">By Team</SelectItem>
                  <SelectItem value="project">By Project</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {userFilterType === "team" && (
              <div className="space-y-1.5 w-48">
                <span className="text-xs font-semibold text-muted-foreground">Select Team</span>
                <Select value={userTeamId} onValueChange={setUserTeamId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {userFilterType === "project" && (
              <div className="space-y-1.5 w-48">
                <span className="text-xs font-semibold text-muted-foreground">Select Project</span>
                <Select value={userProjectId} onValueChange={setUserProjectId}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5 w-40">
              <span className="text-xs font-semibold text-muted-foreground">From Date</span>
              <input
                type="date"
                value={userFromDate}
                onChange={(e) => setUserFromDate(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              />
            </div>

            <div className="space-y-1.5 w-40">
              <span className="text-xs font-semibold text-muted-foreground">To Date</span>
              <input
                type="date"
                value={userToDate}
                onChange={(e) => setUserToDate(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              />
            </div>
          </div>

          {userWorkReport && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 flex flex-col justify-center">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Total Estimated Hours</span>
                  <span className="text-2xl font-bold font-heading mt-1">{userWorkReport.total_estimated_hours}h</span>
                </Card>
                <Card className="p-4 flex flex-col justify-center">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Actual Hours in Range</span>
                  <span className="text-2xl font-bold font-heading mt-1 text-primary">{userWorkReport.total_actual_hours_in_range}h</span>
                </Card>
              </div>

              {/* Detailed Tasks Table */}
              <Card className="p-5 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Tasks Details ({userWorkReport.tasks.length} tasks)</div>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead className="bg-muted/40 font-bold border-b border-border text-muted-foreground">
                      <tr>
                        <th className="p-3">Task Title</th>
                        <th className="p-3">Project</th>
                        <th className="p-3">Team</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Priority</th>
                        <th className="p-3 text-right">Est. Hours</th>
                        <th className="p-3 text-right">Hours in Range</th>
                        <th className="p-3 text-right">Total Hours</th>
                        <th className="p-3 text-right">Age</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userWorkReport.tasks.map((t, idx) => (
                        <tr key={idx} className="border-b border-border hover:bg-muted/20 last:border-0">
                          <td className="p-3 font-semibold text-primary">{t.title}</td>
                          <td className="p-3 text-muted-foreground">{t.project_name}</td>
                          <td className="p-3 text-muted-foreground">{t.team_name}</td>
                          <td className="p-3">
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-border uppercase bg-muted/40">
                              {TASK_STATUS_LABELS[t.status] || t.status}
                            </span>
                          </td>
                          <td className="p-3 uppercase font-medium">{t.priority}</td>
                          <td className="p-3 text-right font-mono">{t.estimated_hours}h</td>
                          <td className="p-3 text-right font-mono text-primary font-semibold">{t.actual_hours_in_range}h</td>
                          <td className="p-3 text-right font-mono">{t.total_hours}h</td>
                          <td className="p-3 text-right font-mono text-muted-foreground">{t.pending_days > 0 ? `${t.pending_days}d` : "0d"}</td>
                        </tr>
                      ))}
                      {userWorkReport.tasks.length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-6 text-center text-muted-foreground italic">No tasks found matching the filter criteria.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* User Activities Feed */}
              <Card className="p-5 space-y-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">User Activity Feed (Date Range)</div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {userWorkReport.activities.map((a, idx) => (
                    <div key={idx} className="p-2.5 rounded border border-border flex items-center justify-between text-xs bg-muted/10 hover:bg-muted/20">
                      <span className="font-medium">{a.description}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">{new Date(a.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                  {userWorkReport.activities.length === 0 && (
                    <div className="text-center py-6 text-xs text-muted-foreground italic">No logged activity in this date range.</div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="team" className="space-y-4">
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-72" data-testid="report-team-select"><SelectValue /></SelectTrigger>
            <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          {teamReport && (
            <Card className="p-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Tasks by Status · {teamReport.total_tasks} total</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={Object.entries(teamReport.tasks_by_status).map(([k, v]) => ({ name: TASK_STATUS_LABELS[k] || k, value: v }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                  <Bar dataKey="value" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="project" className="space-y-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-72" data-testid="report-project-select"><SelectValue /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          {projectReport && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Tasks by Status</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={Object.entries(projectReport.tasks_by_status).map(([k, v]) => ({ name: TASK_STATUS_LABELS[k] || k, value: v }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                    <Bar dataKey="value" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Bugs by Status · {projectReport.total_bugs} total</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={Object.entries(projectReport.bugs_by_status).map(([k, v]) => ({ name: k, value: v }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                    <Bar dataKey="value" fill="hsl(var(--chart-5))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
